/**
 * RS compact countdown — labeled DAY/HRS/MIN/SEC units.
 * Auto-shows at start_date, gracefully hides at end_date.
 * Optional progress bar via [data-progress-bar].
 */
(function () {
  "use strict";

  function pad(value) {
    return String(Math.max(0, value)).padStart(2, "0");
  }

  function parseDate(value) {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  class RsCountdown extends HTMLElement {
    connectedCallback() {
      this.endDate = parseDate(this.dataset.end);
      this.startDate = parseDate(this.dataset.start);
      this.showDays = this.dataset.showDays !== "false";
      this.hideTarget = this.dataset.hideTarget
        ? this.closest(this.dataset.hideTarget) || this
        : this;
      this.progressBar = this.querySelector("[data-progress-bar]");
      this.parts = {
        days: this.querySelector('[data-unit="days"]'),
        hours: this.querySelector('[data-unit="hours"]'),
        minutes: this.querySelector('[data-unit="minutes"]'),
        seconds: this.querySelector('[data-unit="seconds"]'),
      };

      if (!this.endDate) {
        this.hide(true);
        return;
      }

      this.tick();
      this.interval = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.interval);
    }

    emitChange() {
      this.dispatchEvent(
        new CustomEvent("rs:countdown-change", {
          bubbles: true,
          detail: { active: !this.hideTarget.hidden },
        })
      );
    }

    show() {
      const wasHidden = this.hideTarget.hidden;
      this.hideTarget.hidden = false;
      this.hidden = false;
      this.hideTarget.classList.remove("is-pending", "is-expired", "is-fading");
      this.classList.remove("is-pending", "is-expired");
      this.hideTarget.classList.add("is-active");
      this.classList.add("is-active");
      if (wasHidden) this.emitChange();
    }

    hide(immediate) {
      this.classList.remove("is-active", "is-pending");
      this.classList.add("is-expired");
      this.hideTarget.classList.remove("is-active", "is-pending");
      this.hideTarget.classList.add("is-expired");

      const finish = () => {
        this.hideTarget.hidden = true;
        this.hidden = true;
        this.hideTarget.classList.remove("is-fading");
        this.classList.remove("is-fading");
        this.emitChange();
      };

      if (immediate) {
        finish();
        return;
      }

      this.hideTarget.classList.add("is-fading");
      this.classList.add("is-fading");
      window.setTimeout(finish, 350);
    }

    updateProgress(now, start, end) {
      if (!this.progressBar) return;

      const total = Math.max(1, end - (start || end - 24 * 60 * 60 * 1000));
      const remaining = Math.max(0, end - now);
      const ratio = Math.min(1, remaining / total);
      this.progressBar.style.width = `${ratio * 100}%`;
    }

    tick() {
      const now = Date.now();
      const end = this.endDate.getTime();
      const start = this.startDate ? this.startDate.getTime() : 0;

      if (now >= end) {
        window.clearInterval(this.interval);
        this.hide(false);
        return;
      }

      if (start && now < start) {
        const wasVisible = !this.hideTarget.hidden;
        this.hideTarget.hidden = true;
        this.hidden = true;
        this.hideTarget.classList.add("is-pending");
        this.classList.add("is-pending");
        this.hideTarget.classList.remove("is-active");
        this.classList.remove("is-active");
        if (wasVisible) this.emitChange();
        return;
      }

      this.show();
      this.updateProgress(now, start, end);

      let remaining = Math.max(0, Math.floor((end - now) / 1000));
      const days = Math.floor(remaining / 86400);
      remaining -= days * 86400;
      let hours = Math.floor(remaining / 3600);
      remaining -= hours * 3600;
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;

      if (!this.showDays) {
        hours += days * 24;
      }

      if (this.parts.days) this.parts.days.textContent = pad(days);
      if (this.parts.hours) this.parts.hours.textContent = pad(hours);
      if (this.parts.minutes) this.parts.minutes.textContent = pad(minutes);
      if (this.parts.seconds) this.parts.seconds.textContent = pad(seconds);

      const label =
        this.querySelector(".rs-countdown__label")?.textContent?.trim() || "Ends in";
      const readable = `${days}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
      this.setAttribute("aria-label", `${label} ${readable}`);
    }
  }

  if (!customElements.get("rs-countdown")) {
    customElements.define("rs-countdown", RsCountdown);
  }
})();
